import React from 'react';
import { Box, Typography, LinearProgress, Chip, useTheme } from '@mui/material';
import { calculateTokenInfo, TokenInfo } from '../utils/tokenCounter';

interface TokenCounterProps {
    text: string;
    maxTokens?: number;
    showProgress?: boolean;
    showChip?: boolean;
    variant?: 'default' | 'compact';
}

const TokenCounter: React.FC<TokenCounterProps> = ({
    text,
    maxTokens = 200000,
    showProgress = true,
    showChip = false,
    variant = 'default'
}) => {
    const theme = useTheme();
    const tokenInfo: TokenInfo = calculateTokenInfo(text, maxTokens);
    const { characters, tokens, maxTokens: max, remainingTokens, isOverLimit, percentage } = tokenInfo;

    const getColor = () => {
        if (isOverLimit) return 'error';
        if (percentage > 80) return 'warning';
        return 'primary';
    };

    const getProgressColor = () => {
        if (isOverLimit) return 'error';
        if (percentage > 80) return 'warning';
        return 'primary';
    };

    if (variant === 'compact') {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                    variant="caption"
                    color={isOverLimit ? 'error' : 'text.secondary'}
                    sx={{ fontSize: '0.75rem' }}
                >
                    {characters} chars | {tokens}/{max} tokens
                </Typography>
                {showChip && (
                    <Chip
                        label={isOverLimit ? 'Limite excedido' : `${remainingTokens} restantes`}
                        size="small"
                        color={getColor() as any}
                        variant={isOverLimit ? 'filled' : 'outlined'}
                    />
                )}
            </Box>
        );
    }

    return (
        <Box sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography
                    variant="caption"
                    color={isOverLimit ? 'error' : 'text.secondary'}
                >
                    {characters} caracteres | {tokens}/{max} tokens
                </Typography>
                <Typography
                    variant="caption"
                    color={isOverLimit ? 'error' : 'text.secondary'}
                >
                    {isOverLimit ? 'Limite excedido' : `${remainingTokens} tokens restantes`}
                </Typography>
            </Box>

            {showProgress && (
                <LinearProgress
                    variant="determinate"
                    value={Math.min(100, percentage)}
                    color={getProgressColor() as any}
                    sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'grey.200',
                        '& .MuiLinearProgress-bar': {
                            borderRadius: 3
                        }
                    }}
                />
            )}

            {showChip && (
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                    <Chip
                        label={isOverLimit ? 'Limite excedido' : `${Math.round(percentage)}% usado`}
                        size="small"
                        color={getColor() as any}
                        variant={isOverLimit ? 'filled' : 'outlined'}
                    />
                </Box>
            )}
        </Box>
    );
};

export default TokenCounter;
